/** Shared SRS player endpoints (same as desktop AudioDrawer). */
export const SRS_SDK_URL = 'https://squawk.steamroomtrading.com/players/js/srs.sdk.js';
export const SRS_ADAPTER_URL =
  'https://squawk.steamroomtrading.com/players/js/adapter-7.4.0.min.js';

export const STEAMROOM_STREAM_URL =
  'webrtc://squawk.steamroomtrading.com/live/steamroom';
export const ZERODAY_STREAM_URL =
  'webrtc://squawk.steamroomtrading.com/live/zeroday';

/** How long to wait for sdk.play before surfacing error (mobile ICE can stall). */
export const SQUAWK_CONNECT_TIMEOUT_MS = 15_000;

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    SrsRtcPlayerAsync: any;
  }
}

let scriptsPromise: Promise<void> | null = null;

function loadScriptOnce(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    );
    if (existing) {
      // Tag may exist while still loading (second stream raced the first).
      if (existing.dataset.loaded === '1') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error(`Failed to load ${src}`)),
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = '1';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Load adapter + SRS SDK once for all mobile streams.
 * Avoids the race where a second hook sees a <script> tag before onload
 * and sets sdkReady before `SrsRtcPlayerAsync` exists.
 */
export function ensureSrsSdk(): Promise<void> {
  if (!scriptsPromise) {
    scriptsPromise = (async () => {
      await loadScriptOnce(SRS_ADAPTER_URL);
      await loadScriptOnce(SRS_SDK_URL);
      if (typeof window.SrsRtcPlayerAsync !== 'function') {
        scriptsPromise = null;
        throw new Error('SRS SDK loaded but SrsRtcPlayerAsync is missing');
      }
    })().catch((err: unknown) => {
      scriptsPromise = null;
      throw err;
    });
  }
  return scriptsPromise;
}

/** One AudioContext for both mobile streams (iOS handles multi-context poorly). */
let sharedAudioCtx: AudioContext | null = null;

export function getSharedAudioContext(): AudioContext {
  if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
    sharedAudioCtx = new AudioContext();
  }
  return sharedAudioCtx;
}

export async function resumeSharedAudioContext(): Promise<void> {
  const ctx = getSharedAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
}
